export interface RotationInfo {
	rotation: number;
	isFlip: boolean;
}

export const rotationStringValues = ["0", "90", "180", "270", "0F", "90F", "180F", "270F"] as const;
export type RotationString = (typeof rotationStringValues)[number];
const rotationStringSet = new Set<RotationString>(rotationStringValues);

export const isRotationString = (value: unknown): value is RotationString => {
	if (typeof value !== "string") {
		return false;
	}

	return rotationStringSet.has(value as RotationString);
};

export const parseRotation = (rotationString: string | null | undefined): RotationInfo => {
	if (!isRotationString(rotationString)) {
		return { rotation: 0, isFlip: false };
	}

	const isFlip = rotationString.endsWith("F");
	return {
		rotation: parseInt(isFlip ? rotationString.slice(0, -1) : rotationString, 10),
		isFlip,
	};
};

export const isOptimalRotation = (rotationString: RotationString | string | null | undefined): boolean =>
	!rotationString;

export const createRotationString = (angle: number, isFlip: boolean = false): RotationString => {
	const normalizedAngle = ((angle % 360) + 360) % 360;
	const validAngles = [0, 90, 180, 270];

	if (!validAngles.includes(normalizedAngle)) {
		throw new Error(`Invalid rotation angle: ${angle}. Must be 0, 90, 180, or 270.`);
	}

	return `${normalizedAngle}${isFlip ? "F" : ""}` as RotationString;
};

export const toggleFlip = (rotationString: RotationString | "" | null | undefined): string => {
	if (!rotationString) {
		return "0F";
	}

	const { rotation, isFlip } = parseRotation(rotationString);
	return createRotationString(rotation, !isFlip);
};

export const rotateBy90Degrees = (
	rotationString: RotationString | "" | null | undefined,
	clockwise: boolean = true,
): string => {
	if (!rotationString) {
		return clockwise ? "90" : "270"; // If no rotation set, start from 0°
	}

	const { rotation, isFlip } = parseRotation(rotationString);
	const increment = clockwise ? 90 : -90;
	const newRotation = (((rotation + increment) % 360) + 360) % 360; // Handle negative values

	return createRotationString(newRotation, isFlip);
};
